import { useState, useCallback } from "react";
import { literals } from "@/constants/literals";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import {
  editOrderAfterRequest,
  getNumberPhotosInOrder,
  getNumberPhotosInGroup,
  getAllStepsNumberPhotos,
} from "@/lib/services/images";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import type { RequestPhotoInfo } from "@/types/types.d";
import ConfirmTotalImagesContent from "./ConfirmTotalImagesContent";
import { useUserStore } from "@/store/useUser";
import RequestEditionInputs from "./RequestEditionInputs";

export type RequestEditionModalContentProps = {
  photoURL: string;
  image: RequestPhotoInfo;
  loadRequests: () => Promise<void>;
  closeModal: () => void;
  deselectPhoto: () => void;
  showConfirmModal: (newState: boolean) => void;
};

const RequestEditionModalContent = ({
  photoURL,
  image,
  loadRequests,
  closeModal,
  deselectPhoto,
  showConfirmModal,
}: RequestEditionModalContentProps) => {
  const { isSupervisor } = useUserStore();
  const [editedData, setEditedData] = useState(
    JSON.parse(JSON.stringify(image)),
  );
  const [totalImagesContent, setTotalImagesContent] = useState<null | number>(
    null,
  );
  const [editionBody, setEditionBody] = useState<any>(null);

  const changeEditedData = useCallback(
    (
      value: string | boolean | undefined | null,
      field: keyof RequestPhotoInfo,
    ) => {
      setEditedData((previousEditedData: any) => {
        if (
          value === "" &&
          (field === "sapWorkOrder" ||
            field === "document9r1" ||
            field === "customer")
        ) {
          return {
            ...previousEditedData,
            [field]: value,
          };
        }
        if (!image[field] && value === "") {
          return {
            ...previousEditedData,
            [field]: image[field],
          };
        }
        return {
          ...previousEditedData,
          [field]: value,
        };
      });
    },
    [image],
  );

  const { upLoader, downLoader } = useLoader();

  const askTotalImages = async () => {
    const body: any = {
      status: "APPROVED",
    };

    for (const property in image) {
      if (image[property as keyof RequestPhotoInfo] !== editedData[property]) {
        body[property] = editedData[property];
      }
    }

    if (
      Object.keys(body).some(
        (propertyKey) =>
          propertyKey !== "imageGroupDescription" &&
          propertyKey !== "imageGroupVisible" &&
          propertyKey !== "status",
      )
    )
      body.orderId = image.orderId;

    try {
      upLoader();
      if (!body.orderId) {
        const groupCount = await getNumberPhotosInGroup(
          image.imageGroupId.toString(),
        );
        setTotalImagesContent(groupCount.count);
        showConfirmModal(true);
      } else {
        const propertysKeys = Object.keys(body);
        if (
          propertysKeys.includes("orderIn") ||
          propertysKeys.includes("orderOut") ||
          propertysKeys.includes("serialIn") ||
          propertysKeys.includes("serialOut")
        ) {
          const orderCount = await getAllStepsNumberPhotos(image.orderId);
          setTotalImagesContent(orderCount.count);
        } else {
          const orderCount = await getNumberPhotosInOrder(image.orderId);
          setTotalImagesContent(orderCount.count);
        }
        showConfirmModal(true);
      }
      setEditionBody(body);
    } catch {
      appToast.error(literals.aProblem, literals.getNecesaryInfoError);
    } finally {
      downLoader();
    }
  };

  const closeTotalImagesQuestion = () => {
    setTotalImagesContent(null);
    setEditionBody(null);
    showConfirmModal(false);
  };

  const answerPetition = async (status: "APPROVED" | "REJECTED") => {
    if (!isSupervisor) {
      appToast.error(literals.aProblem, literals.notAllowed);
      return;
    }
    if (!image) return;

    if (status === "REJECTED") {
      try {
        upLoader();

        const body: any = {
          status: status,
        };

        await editOrderAfterRequest(image.requestId, body);
        appToast.success(literals.completedProcess, literals.editionRejected);
        await loadRequests();
        closeModal();
        deselectPhoto();
      } catch {
        appToast.error(literals.aProblem, literals.requestError);
      } finally {
        downLoader();
      }
      return;
    }

    if (disableButton()) {
      return;
    }

    if (editionBody === null) {
      askTotalImages();
      return;
    }

    try {
      upLoader();

      const editionResult = await editOrderAfterRequest(
        image.requestId,
        editionBody,
      );

      if (
        !editionResult.orderResponseApi?.warning &&
        !editionResult.orderResponseApi?.restrict
      ) {
        appToast.success(literals.completedProcess, literals.editedPhoto);
      } else {
        appToast.error(
          literals.rejectedPetition,
          editionResult.orderResponseApi?.message || literals.requestError,
        );
      }
      await loadRequests();
      closeModal();
      deselectPhoto();
    } catch {
      appToast.error(literals.aProblem, literals.requestError);
    } finally {
      downLoader();
    }
    showConfirmModal(false);
    setEditionBody(null);
  };

  const disableButton = () => {
    const _brokeFieldCondition = (
      field: keyof RequestPhotoInfo,
      minLength: number,
      maxLength: number,
    ) => {
      const _asString = (value: string | null | undefined) =>
        typeof value === "string" ? value : "";

      const _isValid = (stringValue: string) =>
        stringValue.length >= minLength && stringValue.length <= maxLength;

      const original = _asString(image[field] as keyof RequestPhotoInfo);
      const edited = _asString(editedData[field]);

      const editedValid = _isValid(edited);

      if (original === "" && edited === "") return false;
      return !editedValid;
    };

    const _replaceNulls = (object: any) => {
      const newObject = JSON.parse(JSON.stringify(object));
      for (const property in newObject) {
        if (newObject[property] === null) {
          newObject[property] = "";
        }
      }
      return newObject;
    };

    return (
      JSON.stringify(_replaceNulls(editedData)) ===
        JSON.stringify(_replaceNulls(image)) ||
      ((editedData.step === literals.inNoServ ||
        editedData.step === literals.packagingAction) &&
        (!editedData.imageGroupDescription?.length ||
          editedData.imageGroupDescription.startsWith(":"))) ||
      _brokeFieldCondition("sapWorkOrder", 10, 12) ||
      _brokeFieldCondition("document9r1", 10, 10) ||
      _brokeFieldCondition("customer", 3, 6)
    );
  };

  return !totalImagesContent ? (
    <>
      <RequestEditionInputs
        photoURL={photoURL}
        image={image}
        editedData={editedData}
        changeEditedData={changeEditedData}
      />
      <div className="w-full border-t border-t-ib-gray-borders flex flex-row justify-between items-center pt-4">
        <div className="text-ib-pt-lg text-ib-gray-main">
          {literals.wishEditPhoto}
        </div>
        <div className="flex flex-row gap-x-4 justify-start">
          <div className="w-40">
            <SecondaryButton
              size="medium"
              colour="red"
              label={literals.rejectPetition}
              buttonType="button"
              onClick={() => answerPetition("REJECTED")}
            />
          </div>
          <div className="w-40">
            <PrimaryButton
              size="medium"
              colour="red"
              label={literals.edit}
              type="button"
              onClick={() => answerPetition("APPROVED")}
              disabled={disableButton()}
            />
          </div>
        </div>
      </div>
    </>
  ) : (
    <ConfirmTotalImagesContent
      closeQuestion={closeTotalImagesQuestion}
      totalImagesAffected={totalImagesContent}
      continueEdition={() => answerPetition("APPROVED")}
    />
  );
};
export default RequestEditionModalContent;
